import React, { FC } from 'react';
import { useParams } from 'react-router';
import { ViewType } from 'api/views/types';
import { Filter } from 'components/molecules/filters/types';
import { SavedViews as ViewsComponent } from 'components/molecules/views';
import { useProjectViewActions } from 'components/molecules/views/project-view-actions';
import { useViewActions } from 'components/molecules/views/view-actions';
import { TaskFilterPopover } from 'components/organisms/tasks/filter-popover';
import {
  useUpdateProjectTaskViewMutation,
  useUpdateTaskViewMutation,
} from 'queries/task-view';
import {
  useAllTasksKanbanGroupBy,
  useFilterMenu,
} from '../filter-menu-sidebar';
import { ColumnOrder } from '../grid-custom-column';
import { FilterableField } from './fields';
import { isValidViewDefinition, viewDefinitionComparator } from './views';

export interface Props {
  isFieldsLoading: boolean;
  filter: Filter;
  fields: FilterableField[];
  selectFilter: (filter: Filter) => void;
  clearFilters: () => void;
  lookup: Record<string, FilterableField>;
  includeSavedViews?: boolean;
  columnOrder?: ColumnOrder[];
  defaultColumnOrder?: ColumnOrder[];
  handleColumnOrderChange?: (columnOrder: ColumnOrder[]) => void;
}

export const TaskFilterView: FC<Props> = ({
  filter,
  fields,
  isFieldsLoading,
  selectFilter,
  clearFilters,
  lookup,
  includeSavedViews,
  columnOrder = [],
  defaultColumnOrder = [],
  handleColumnOrderChange = () => {},
}) => {
  const [groupBy, setGroupBy] = useAllTasksKanbanGroupBy(
    lookup,
    isFieldsLoading,
  );
  const { projectId } = useParams<{ projectId: string }>();
  const {
    taskListViews,
    viewDefinitionTransformer,
    currentViewId,
    viewContextValue,
    onViewSelect,
    defaultViewsList,
    defaultViewId,
  } = useFilterMenu({
    fields,
    lookup,
    isFieldsLoading,
    setKanbanBoardGroupChangeLoading: () => {},
    selectFilter,
    clearFilters,
    setGroupBy,
    projectId,
    handleColumnOrderChange,
    defaultColumnOrder,
  });

  const { onUpdate: onTaskViewUpdate } = useUpdateTaskViewMutation();

  const { onViewCreate, onViewUpdate, onViewDelete } = useViewActions({
    views: taskListViews,
    setViews: onTaskViewUpdate,
    viewType: ViewType.AllTasks,
    transformer: viewDefinitionTransformer,
  });

  const { onUpdate: onProjectTaskViewUpdate } =
    useUpdateProjectTaskViewMutation(projectId);

  const { onProjectViewCreate, onProjectViewUpdate } = useProjectViewActions({
    views: taskListViews,
    setViews: onProjectTaskViewUpdate,
    viewType: ViewType.ProjectTaskList,
    transformer: viewDefinitionTransformer,
  });

  return (
    <ViewsComponent
      contextValue={viewContextValue}
      filterComponent={
        <TaskFilterPopover
          filter={filter}
          fields={fields}
          loading={isFieldsLoading}
          onChange={selectFilter}
          reset={clearFilters}
          hideClearFilter
        />
      }
      views={taskListViews}
      defaultViewsList={defaultViewsList}
      defaultViewId={defaultViewId}
      currentViewDefinition={{
        filter,
        groupBy,
        columnOrder,
      }}
      onViewSelect={onViewSelect}
      isValidViewDefinition={isValidViewDefinition(defaultColumnOrder)}
      viewDefinitionComparator={viewDefinitionComparator(defaultColumnOrder)}
      onViewCreate={onViewCreate}
      onViewDelete={onViewDelete}
      onViewUpdate={onViewUpdate}
      onProjectViewCreate={onProjectViewCreate}
      onProjectViewUpdate={onProjectViewUpdate}
      selectedViewId={currentViewId}
      includeSavedViews={includeSavedViews ?? false}
    />
  );
};
